import { Inject, Injectable } from '@angular/core';

import { RaceEntity, RacingProviders } from '@cds/betting-offer/domain-specific/horse-racing';
import { BetFilterCount, BetFilterRequest } from '@cds/betting-offer/domain-specific/horse-racing/racing-post';
import { TagCount, TagType } from '@cds/betting-offer/tags';
import { DetailedCreateFactory, DetailedGroupingFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EnhancedFixtureViewGroupingConfiguration } from '@frontend/sports/betting-offer/feature/model';
import { OfferGroupingService } from '@frontend/sports/betting-offer/feature/offer-grouping';
import { BettingOfferApi } from '@frontend/sports/betting-offer/feature/offer-service';
import { RacingConfig } from '@frontend/sports/common/client-config-data-access';
import { BaseCdsApiFactory, CDS_API_FACTORY } from '@frontend/sports/content-distribution/feature';
import { WinGameFixture, toRacingPostFixture } from '@frontend/sports/racing/feature/models';
import { Observable, forkJoin, map, of, switchMap } from 'rxjs';

import { AbstractRacingService } from './racing-api.service';

@Injectable({
    providedIn: 'root',
})
export class HorseRacingPostApiService extends AbstractRacingService {
    ToFixture(raceEntity: RaceEntity): WinGameFixture {
        return toRacingPostFixture(raceEntity);
    }

    provider(): RacingProviders {
        return RacingProviders.RacingPost;
    }

    constructor(
        @Inject(CDS_API_FACTORY) cdsApiServiceFactory: BaseCdsApiFactory,
        protected override racingConfig: RacingConfig,
        protected override offerGroupingService: OfferGroupingService,
        protected override createFactory: DetailedCreateFactory,
        protected override groupingFactory: DetailedGroupingFactory,
        private apiService: BettingOfferApi,
    ) {
        super(cdsApiServiceFactory, racingConfig, '/horse-racing', offerGroupingService, createFactory, groupingFactory);
    }

    getBetFilter(request: BetFilterRequest): Observable<WinGameFixture[]> {
        return this.cdsApi.post<RaceEntity[]>('/bet-filter?marketTypes=WinEW&offerMapping=Filtered', request).pipe(
            switchMap((fixtures) => {
                if (!fixtures) {
                    return of([]);
                }
                return this.mapFixtures(fixtures);
            }),
        );
    }

    getBetFilterCount(request: BetFilterRequest): Observable<number> {
        return this.cdsApi.post<BetFilterCount>('/bet-filter-count', request).pipe(
            map((resultCount) => {
                return resultCount ? resultCount.count : 0;
            }),
        );
    }

    getPredictors(fixtureId: string): Observable<WinGameFixture> {
        const empty = {} as WinGameFixture;
        return this.cdsApi.get<RaceEntity[]>(`/horse-predictor/${fixtureId}?marketTypes=WinEW&offerMapping=Filtered`).pipe(
            switchMap((fixtures) => {
                if (!fixtures) {
                    return of(empty);
                }
                return this.mapFixture(fixtures![0]);
            }),
        );
    }

    getAntepostGroupingData(
        sport: number,
    ): Observable<{ grouping: EnhancedFixtureViewGroupingConfiguration | undefined; counts: TagCount[] | undefined }> {
        return forkJoin([
            this.offerGroupingService.getFixtureGrouping(sport, 'any'),
            this.apiService.getCountsPerSport(sport, TagType.FixtureGroup),
        ]).pipe(
            map(([grouping, counts]) => {
                return { grouping, counts };
            }),
        );
    }
}
